// Neutrální registr „ObjectCategory → native agenda" — umožňuje postupně nahrazovat legacy agendy
// phoenix native agendami, aniž by o tom věděly selektory nebo legacy strana. Po zaregistrování
// jedné položky (např. `Station`) se na native agendy přesměrují VŠECHNY navigace té ObjectCategory:
//   1) ⋯ (výběrový picker) a proklik do detailu v selektorech (@/shared/ui/async-selector),
//   2) phoenixem iniciované otevření detailu (sendMagicFormEdit — search box, ListPage onOpen),
//   3) navigace iniciované uvnitř legacy (Magic `form/open` v MagicControlBridge).
//
// Store i funkce záměrně NEZÁVISÍ na navigation/ui/windowing vrstvách (žádný import odtud) → žádný
// import cyklus a shared/ui zůstává navigation-agnostické (stejná disciplína jako `layerReload`).
// Konkrétní mapping (agenda handly + kernel) instaluje app/navigační vrstva přes
// `registerObjectCategoryNav` (viz `installObjectCategoryNav` v app/router) — sem přitéká už jen
// erasovaná imperativní closure + pure-ui picker komponenta.

import type { ComponentType } from 'react';
import { create } from 'zustand';

/** Odvodí ObjectCategory z GlobalName (část před první tečkou). Např. `Station.List` → `Station`. */
export function categoryOf(globalName: string): string {
  const dot = globalName.indexOf('.');
  return dot === -1 ? globalName : globalName.slice(0, dot);
}

/** layerId zdrojové vrstvy (kvůli dědění floating stacku), nebo `null` při otevření mimo vrstvu. */
export type ObjectCategorySourceLayerId = string | null;

/**
 * Props výběrového (picker) okna ListPage — strukturálně shodné s `ListPagePickerProps`
 * z async-selectoru. Duplikujeme je zde záměrně, aby neutrální lib neimportoval z `shared/ui`.
 */
export interface ObjectCategoryPickerProps {
  open: boolean;
  onClose: () => void;
  initialSelectedIds: (string | number)[];
  onConfirm: (ids: (string | number)[]) => void;
}

/** Jedna položka registru — instaluje ji navigační vrstva (closures nad kernelem). */
export interface ObjectCategoryNavEntry {
  /** Otevře native detail (create/edit) záznamu daného `id`. */
  readonly openDetail: (id: string | number, sourceLayerId?: ObjectCategorySourceLayerId) => void;
  /** Otevře native list (browse / root). Volitelné — je-li k dispozici native listová agenda. */
  readonly openList?: (sourceLayerId?: ObjectCategorySourceLayerId) => void;
  /** Výběrový (picker) režim native ListPage pro akci ⋯ v selektoru (pure-ui komponenta). */
  readonly picker?: ComponentType<ObjectCategoryPickerProps>;
}

interface ObjectCategoryNavState {
  readonly entries: Readonly<Record<string, ObjectCategoryNavEntry>>;
  register: (objectCategory: string, entry: ObjectCategoryNavEntry) => void;
  unregister: (objectCategory: string) => void;
}

export const useObjectCategoryNavStore = create<ObjectCategoryNavState>((set) => ({
  entries: {},
  register: (objectCategory, entry) =>
    set((s) => ({ entries: { ...s.entries, [objectCategory]: entry } })),
  unregister: (objectCategory) =>
    set((s) => {
      if (!(objectCategory in s.entries)) return s;
      const next = { ...s.entries };
      delete next[objectCategory];
      return { entries: next };
    }),
}));

function entryOf(objectCategory: string | undefined): ObjectCategoryNavEntry | undefined {
  if (objectCategory == null) return undefined;
  return useObjectCategoryNavStore.getState().entries[objectCategory];
}

// ---- Registrace (volá se z app/navigační vrstvy) ----

/** Zaregistruje native mapping pro danou ObjectCategory. */
export function registerObjectCategoryNav(
  objectCategory: string,
  entry: ObjectCategoryNavEntry
): void {
  useObjectCategoryNavStore.getState().register(objectCategory, entry);
}

// ---- Neutrální getters (mimo React) — pro sendMagicFormEdit / MagicControlBridge ----

/** Má daná ObjectCategory zaregistrovanou native agendu? */
export function hasObjectCategoryNav(objectCategory: string | undefined): boolean {
  return entryOf(objectCategory) != null;
}

/**
 * Otevře native detail dané ObjectCategory. Vrací `true`, pokud native mapping existuje a převzal
 * navigaci; `false` = volající má spadnout na legacy (Magic `form/edit`).
 */
export function openObjectCategoryDetail(
  objectCategory: string | undefined,
  id: string | number,
  sourceLayerId?: ObjectCategorySourceLayerId
): boolean {
  const entry = entryOf(objectCategory);
  if (!entry) return false;
  entry.openDetail(id, sourceLayerId);
  return true;
}

/**
 * Otevře native list dané ObjectCategory. Vrací `true`, pokud native mapping (s listovou agendou)
 * převzal navigaci; `false` = volající má spadnout na legacy.
 */
export function openObjectCategoryList(
  objectCategory: string | undefined,
  sourceLayerId?: ObjectCategorySourceLayerId
): boolean {
  const entry = entryOf(objectCategory);
  if (!entry?.openList) return false;
  entry.openList(sourceLayerId);
  return true;
}

// ---- Reaktivní hooky (pro async-selector) ----

/** Reaktivně vrátí picker komponentu native ListPage pro ⋯, nebo `undefined`. */
export function useObjectCategoryPicker(
  objectCategory: string | undefined
): ComponentType<ObjectCategoryPickerProps> | undefined {
  return useObjectCategoryNavStore((s) =>
    objectCategory != null ? s.entries[objectCategory]?.picker : undefined
  );
}
